import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import {
  Box,
  Button,
  ButtonGroup,
  LinearProgress,
  ListItem,
  ListItemText,
  TablePagination,
  TableSortLabel
} from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import ViewListIcon from '@material-ui/icons/ViewList';

import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import { PaginatedList } from '../../common/PaginatedList.interface';
import SearchBox from '../../common/SearchBox';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import RiskDialog from './components/RiskDialog';
import RiskPill from './components/RiskPill';
import { useFindRisks } from './Risk.hook';
import { Risk } from './Risk.interface';
import TaskBoard from '../tasks/TaskBoard';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import ListMoreButtonMenu from '../../common/ListMoreButtonMenu';
import RiskExportButton from './components/RiskExportButton';
import { AppSnackbar } from '@sierralabs/react-material-ui';

const StyledTableCell = styled(TableCell)`
  white-space: normal;
  word-wrap: break-word;
`;

enum ViewEnum {
  List = 'list',
  Board = 'board'
}

const RiskManagementListPage: React.FC = () => {
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [view, setView] = useQueryParam('view', StringParam);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    StringParam
  );
  const [includeDeleted, setIncludeDeleted] = useQueryParam(
    'deleted',
    withDefault(BooleanParam, false)
  );
  const history = useHistory();
  const [risks, setRisks] = useState<PaginatedList<Risk>>();
  const [dialog, setDialog] = useState<boolean>(false);
  const { riskList, isLoading, error: loadingError, get } = useFindRisks();

  useEffect(() => {
    const params = {
      search,
      page,
      limit,
      order: String(order || ''),
      includeDeleted
    } as any;
    if (order && orderDirection) {
      params.order = `${order} ${orderDirection}`;
    }
    get(params);
  }, [get, page, limit, order, orderDirection, search, includeDeleted]);

  useEffect(() => {
    riskList && setRisks(riskList);
  }, [riskList]);

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimit(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    loadingError && setErrorMessage(loadingError.message);
  }, [loadingError]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Risk Management</h2>
        <Box ml={2}>
          <ButtonGroup variant='contained' color='primary'>
            <Button size='small' onClick={() => setView(ViewEnum.List)}>
              <ViewListIcon />
            </Button>
            <Button size='small' onClick={() => setView(ViewEnum.Board)}>
              <ViewColumnIcon />
            </Button>
          </ButtonGroup>
        </Box>
        {view !== ViewEnum.Board && (
          <>
            <SearchBox value={search} onSearch={value => setSearch(value)} />
            <PageTemplateHeaderActions>
              <Button
                color='primary'
                variant='contained'
                onClick={() => setDialog(true)}
              >
                New Risk
              </Button>
              {riskList && (
                <RiskExportButton
                  onError={error => setErrorMessage(error.message)}
                />
              )}
              <ListMoreButtonMenu>
                <ListItem
                  button
                  onClick={() => () => setIncludeDeleted(!includeDeleted)}
                >
                  <ListItemText
                    primary={`${includeDeleted ? 'Hide' : 'Show'} Deleted`}
                  />
                </ListItem>
              </ListMoreButtonMenu>
              <RiskDialog
                open={dialog}
                onCancel={() => setDialog(false)}
                onSuccess={risk => {
                  setDialog(false);
                  // redirect to detail page
                  history.push(`/portal/risks/task/${risk.task.id}`);
                }}
              />
            </PageTemplateHeaderActions>
          </>
        )}
      </PageTemplateHeader>
      {view === ViewEnum.Board ? (
        <PageTemplateDashboard>
          <TaskBoard
            entity={WorkflowEntity.Risk}
            onSelectTask={task => {
              const taskId = task.parent?.id || task.id;
              history.push(`/portal/risks/task/${taskId}`);
            }}
          />
        </PageTemplateDashboard>
      ) : (
        <PageTemplateCardOver>
          <TableContainer>
            <Table aria-label='Risk Management Table'>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'id'}
                      direction={getSortDirection('id')}
                      onClick={() => handleSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'title'}
                      direction={getSortDirection('title')}
                      onClick={() => handleSort('title')}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  {/* <TableCell>
                    <TableSortLabel
                      active={order === 'level'}
                      direction={getSortDirection('level')}
                      onClick={() => handleSort('level')}
                    >
                      Risk Level
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'mitigation'}
                      direction={getSortDirection('mitigation')}
                      onClick={() => handleSort('mitigation')}
                    >
                      Mitigation
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'verification'}
                      direction={getSortDirection('verification')}
                      onClick={() => handleSort('verification')}
                    >
                      Verification
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'residual_risk'}
                      direction={getSortDirection('residual_risk')}
                      onClick={() => handleSort('residual_risk')}
                    >
                      Residual Risk
                    </TableSortLabel>
                  </TableCell> */}
                  <TableCell>
                    <TableSortLabel
                      active={order === 'status'}
                      direction={getSortDirection('status')}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {risks &&
                  risks.data.map((risk: Risk) => (
                    <TableRow
                      hover
                      key={risk.id}
                      className={risk.deleted ? 'deleted' : ''}
                      onClick={() =>
                        history.push(`/portal/risks/task/${risk.task.id}`)
                      }
                    >
                      <TableCell>RISK-{risk.id}</TableCell>
                      <StyledTableCell>{risk.title}</StyledTableCell>
                      <TableCell>
                        <TaskStatusLabel task={risk.task} />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component='div'
            count={risks?.total || 0}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowPerPage}
          />
        </PageTemplateCardOver>
      )}
      <AppSnackbar severity='error' open={Boolean(errorMessage)}>
        {errorMessage}
      </AppSnackbar>
    </PageTemplate>
  );
};

export default RiskManagementListPage;
